import { useEffect, useRef, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useStore, useSelector } from 'statux'

import DashboardSettingsInputUpload
    from '../DashboardSettings/DashboardSettingsInputUpload/DashboardSettingsInputUpload'

import Alert from '../../../components/Alert/Alert'
import Details from '../../../components/Details/Details'
import Form from '../../../components/Form/Form'
import RichText from '../../../components/RichText/RichText'
import TextArea from '../../../components/TextArea/TextArea'

import authProvider from '../../../utils/auth-provider'
import { getCssVar } from '../../../utils/css'
import { graphqlClient, gql } from '../../../utils/gql'
import { scrollTo } from '../../../utils/dom'
import { camelCase } from '../../../utils/strings'

export const getEbpcFormDataObject = (
    steps = [],
    requiredOnly = false
) => {
    const data = {}

    if (!Array.isArray(steps))
        return data

    const getFieldNamesArray = (fields = []) => fields
        .map((obj) => {
            if ((obj.fields || []).length)
                return getFieldNamesArray(obj.fields)

            if (requiredOnly
                    && !(((obj || {}).props || {}).required
                        || (obj || {}).required))
                return false

            const name = ((obj || {}).props || {}).name
                || (obj || {}).name

            if (name)
                return camelCase(name)

            return null
        })
        .flat()
        .filter(str => str)

    steps
        .filter(obj => obj.slug)
        .forEach(obj => (data[camelCase(obj.slug)]
            = getFieldNamesArray(obj.fields || [])
                .reduce((acc, curr) => {
                    acc[curr] = null
                    return acc
                }, {})))

    return data
}

export default function DashboardEbpc (props = {
    heading: null,
    richText: null,
    alerts: null,
    steps: null,
    submitSuccessModal: null,
    faq: null
}) {
    let {
        heading,
        richText,
        steps,
        submitSuccessModal,
        faq
    } = props

    faq = faq || {}

    faq.heading = faq.heading || null

    faq.richText = faq.richText || null

    faq.items = faq.items || []

    const formId = 'ebpc-application'

    const el = useRef()

    const {
        ebpcContest,
        ebpcApplicationsOpen,
        ebpcApplicationsEndDateStr
    } = useSelector()

    if (ebpcApplicationsEndDateStr
            && richText)
        richText = richText
            .replace(/(<\/p><small>)/, ` Applications are due ${
                ebpcApplicationsEndDateStr}.$1`)

    const formStateIndicator = useRef()

    const [modal, setModal] = useStore('modal')

    const [successModal, setSuccessModal] = useState()

    const [alerts, setAlerts] = useState([])

    const params = useParams()

    const history = useHistory()

    const [profile, setProfile] = useState(
        (history.location.state || {}).profile
            || {
                id: null,
                ebpc_applications: []
            })

    const [application, setApplication] = useState(
        (history.location.state || {}).application
            || {
                id: null,
                created_at: null,
                submitted_at: null,
                data: getEbpcFormDataObject(steps)
            })

    const [formValid, setFormValid] = useState(false)

    const role = authProvider.getDefaultRole()

    const session = authProvider.getSession() || {}

    // Active, prev and next step
    const activeStep = (steps || [])
        .filter(obj => obj.slug === params.step)[0]

    const prevStep = activeStep
        && steps[steps.indexOf(activeStep) - 1]

    const nextStep = activeStep
        ? steps[steps.indexOf(activeStep) + 1]
        : (steps || [])[0]

    console.groupCollapsed('EBPC Application')
    console.log('Data', application)
    console.log('Active step', activeStep)
    console.log('Previous step', prevStep)
    console.log('Next step', nextStep)
    console.groupEnd()

    const basePathname = (params.step
        ? history.location.pathname
            .replace(new RegExp(`\\/${params.step}\\/*$`), '')
        : history.location.pathname
    ).replace(/\/$/, '')

    const setFormStateIndicator = (state) => formStateIndicator.current
        && (!state
            ? formStateIndicator.current
                .removeAttribute('data-form-state')
            : formStateIndicator.current
                .setAttribute('data-form-state', state))

    // Update interal application state to trigger auto-save
    const onUpdate = (e) => {
        const ebpcApplication = JSON.parse(JSON.stringify(application))
            || { data: {} }

        const { data } = ebpcApplication

        const dataStepKey = e.target.getAttribute('data-step')

        if (!dataStepKey)
            return

        if (!ebpcApplication.data[dataStepKey])
            ebpcApplication.data[dataStepKey] = {}

        const dataPropValue = data[dataStepKey][camelCase(e.target.name)]

        const selector = `[name="${e.target.name}"]`

        const { type } = e.target || { type: null }

        const checkboxes = type === 'checkbox'
                && el.current
            ? Array.from(el.current.querySelectorAll(selector))
            : []

        const isCheckboxArray = checkboxes.length > 1

        const conditionalProp = camelCase(`${e.target.name}-${e.target.value}`)

        const conditionalInput = (type || '').match(/^(checkbox|radio)$/)
            && el.current
            && el.current.querySelector(`[name=${conditionalProp}]`)

        if (conditionalInput) {
            const conditionalStepKey
                = conditionalInput.getAttribute('data-step')

            const conditionalPropValue
                = data[conditionalStepKey][conditionalProp]

            const inputValue = e.target.checked
                ? conditionalInput.value
                : null

            if (conditionalPropValue !== inputValue)
                ebpcApplication.data[conditionalStepKey][conditionalProp]
                    = inputValue
        }

        if (isCheckboxArray
               && !Array.isArray(dataPropValue))
            ebpcApplication.data[dataStepKey][camelCase(e.target.name)] = []

        if (isCheckboxArray) {
            let values = checkboxes
                .filter(_el => _el.checked)
                .map(_el => _el.value || _el.checked)

            if (!values.length)
                values = null

            if (JSON.stringify(dataPropValue) === JSON.stringify(values))
                return

            ebpcApplication.data[dataStepKey][camelCase(e.target.name)]
                = values

            return setApplication(ebpcApplication)
        }

        if (dataPropValue === e.target.value)
            return

        ebpcApplication.data[dataStepKey][camelCase(e.target.name)]
            = e.target.value || null

        setApplication(ebpcApplication)
    }

    const gotoStep = async (step, focusEl) => {
        if (!el.current
                || !step)
            return

        const form = el.current.querySelector('form')

        const formNavLink = el.current
            .querySelector(`nav.dashboard-long-form-nav a[data-step=${step}]`)

        focusEl = focusEl || form

        if (!focusEl
                || !formNavLink)
            return

        const stickyNavOffset = parseFloat(
            getCssVar('--sticky-nav-offset')) || 0

        const scrollToY = window.scrollY <= stickyNavOffset
            ? window.scrollY
            : stickyNavOffset

        const isCurrent = formNavLink.getAttribute('aria-current')
            && formNavLink.getAttribute('aria-current') !== 'false'

        const isAnimate = focusEl === form
            || isCurrent

        await scrollTo([0, scrollToY], {
            cancelOnUserAction: false,
            speed: isAnimate
                ? 200
                : 0,
            minDuration: isAnimate
                ? 250
                : 0
        })

        if (isAnimate)
            focusEl.focus({ preventScroll: true })

        if (isCurrent)
            return

        if (focusEl !== form) {
            if (focusEl.nextElementSibling.type === 'file')
                focusEl = focusEl.nextElementSibling

            const formFieldsInactive = Array.from(el.current.querySelectorAll(
                `.dashboard-long-form-step-fields:not(.display-none):not([data-step=${step}])`))

            const formFields = el.current
                .querySelector(`.dashboard-long-form-step-fields[data-step=${step}]`)

            formFieldsInactive
                .forEach(_el => _el.classList.add('display-none'))

            if (formFields.classList.contains('display-none'))
                formFields.classList.remove('display-none')

            const formFieldsChildren = Array.from(formFields.children)

            const focusElParent = formFieldsChildren
                .filter(_el => _el.contains(focusEl))[0]

            let verticalOffset = formFieldsChildren.indexOf(focusElParent) === 0
                ? 20
                : 30

            if (focusElParent) {
                const { top } = focusEl.getBoundingClientRect()

                const parentBounds = focusElParent.getBoundingClientRect()

                verticalOffset += top - parentBounds.top
            }

            if (verticalOffset < 20)
                verticalOffset = 20

            verticalOffset = verticalOffset * -1

            await scrollTo(focusEl, {
                cancelOnUserAction: false,
                verticalOffset
            })
        }

        return history.push(formNavLink.getAttribute('href'))
    }

    const onScrollToStep = async (e) => {
        const step = e.target.getAttribute('data-step')

        if (!step)
            return

        e.preventDefault()

        e.stopPropagation()

        gotoStep(step)
    }

    const onSubmit = (e) => {
        if (!formValid)
            return

        const ebpcApplication = JSON.parse(JSON.stringify(application))

        ebpcApplication.submitted_at = (new Date()).toISOString()

        setApplication(ebpcApplication)
    }

    useEffect(async () => {
        if (role !== 'member'
                || !(ebpcContest || {}).id)
            return history.push('/dashboard')

        const email = ((session || {}).user || {}).email

        if (!email)
            return

        const queryProfile = await graphqlClient
            .request(gql`
                query {
                    profiles_by_pk (
                        email: "${email}"
                    ) {
                        id
                        ebpc_applications (
                            where: {
                                ebpc_contest_id: { _eq: ${ebpcContest.id} }
                            }
                        ) {
                            id,
                            ebpc_contest_id,
                            created_at,
                            submitted_at,
                            data
                        }
                    }
                }`)
            .then(obj => obj.profiles_by_pk)
            .catch((err) => {
                console.warn(err)
                return false
            })

        setProfile(queryProfile)

        const ebpcApplication = (queryProfile.ebpc_applications || [])[0]

        if (ebpcApplication
                && !ebpcApplication.data)
            ebpcApplication.data = application.data

        if (ebpcApplication)
            setApplication(ebpcApplication)

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                application: ebpcApplication,
                profile: queryProfile
            }))
    }, [])

    useEffect(async () => {
        if (!ebpcApplicationsOpen
                || !(ebpcContest || {}).id
                || !profile.id
                || !activeStep
                || application.id)
            return

        const insertApplication = await graphqlClient
            .request(gql`
                mutation {
                    insert_ebpc_applications_one (object: {
                        profile_id: "${profile.id}",
                        ebpc_contest_id: ${ebpcContest.id}
                    }) {
                        id,
                        created_at
                    }
                }`)
            .then(obj => obj.insert_ebpc_applications_one)
            .catch((err) => {
                console.warn(err)
                return false
            })

        if (insertApplication)
            setApplication({
                id: insertApplication.id,
                created_at: insertApplication.created_at,
                submitted_at: application.submitted_at,
                data: application.data
            })
    }, [ebpcContest, activeStep, application])

    const getInvalidInputs = () => ((el.current
            && Array.from(el.current.querySelectorAll(':invalid'))) || [])
        .filter(input => input.name)
        .filter((input) => {
            const step = input.getAttribute('data-step')

            const appValue
                = (application.data[step] || {})[camelCase(input.name)]

            return !(Array.isArray(appValue)
                ? appValue.length
                : appValue)
        })

    useEffect(() => {
        if (!application.id)
            return

        const applicationAlerts = (props.alerts || [])
            .filter(obj => (obj.type === 'complete'
                    && application.submitted_at)
            || (obj.type === 'incomplete'
                && !application.submitted_at))

        if (applicationAlerts.length !== alerts.length)
            setAlerts(applicationAlerts)

        if (!application.data)
            return

        setFormValid(!getInvalidInputs().length)
    }, [application])

    useEffect(async () => {
        const profileState = JSON.stringify(profile.ebpc_applications[0] || {})

        if (!application
                || !application.id
                || JSON.parse(profileState).submitted_at)
            return

        let updateState = JSON.stringify(application)

        if (updateState === profileState)
            return

        setFormStateIndicator('saving')

        updateState = JSON.parse(updateState)

        console.log('Saving...', updateState)

        const mutation = await graphqlClient
            .request(gql`
                mutation (
                    $data: jsonb,
                ) {
                    update_ebpc_applications (
                        where: { id: { _eq: "${application.id}" } }
                        _set: {
                            data: $data,
                            submitted_at: ${updateState.submitted_at
                                ? `"${updateState.submitted_at}"`
                                : null}
                        }
                    ) {
                        returning {
                            id
                        }
                    }
                }
            `, {
                data: updateState.data
            }, {
                headers: {
                    'x-hasura-role': 'me'
                }
            })
            .then(obj => ((obj.update_ebpc_applications || {})
                .returning || [])[0])
            .catch((err) => {
                console.warn(err)
                return { errors: [] }
            })

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                application: updateState
            }))

        if (!mutation.id) {
            setFormStateIndicator('error')
            setTimeout(() => setFormStateIndicator(), 4000)
            return
        }

        if (updateState.submitted_at) {
            setFormStateIndicator()

            scrollTo([0, parseFloat(getCssVar('--sticky-nav-offset')) || 0], {
                cancelOnUserAction: false
            }).then(() => setSuccessModal(Object.assign({
                className: 'modal-ebpc-application',
                iconBackground: getCssVar('--aqua'),
                heading: 'Thanks for your application!'
            }, submitSuccessModal || {})))

            return
        }

        setFormStateIndicator('saved')

        setTimeout(() => setFormStateIndicator(), 3000)
    }, [application])

    useEffect(() => {
        if (successModal
                && application.submitted_at)
            return setModal(successModal)
    }, [history.location.pathname])

    useEffect(() => {
        if (!successModal
                || !application.submitted_at)
            return

        history.push(basePathname)
    }, [successModal])

    useEffect(() => {
        if (!modal || !successModal)
            return

        setSuccessModal(null)
    }, [modal])

    const renderChoices = (obj = {
        key: null,
        step: null,
        legend: null,
        name: null,
        type: 'radio',
        choices: [],
        required: false,
        validationMessage: '',
        invalid: '',
        disclaimer: ''
    }) => {
        let {
            key,
            step,
            legend,
            name,
            type,
            choices,
            required,
            validationMessage,
            invalid,
            disclaimer
        } = obj

        choices = choices
            .map(item => typeof item === 'string'
                ? ({ value: item, label: item })
                : item)
            .filter(item => 'value' in item)

        if (!step
                || !name
                || !choices.length)
            return null

        const dataValue = (application
            .data[camelCase(step)] || {})[camelCase(name)]

        return (
            <fieldset
                className="form-field margin-3-b"
                key={key}
            >
                {legend
                    && (
                        <legend
                            className="font-weight-medium"
                            aria-describedby={disclaimer
                                && `${formId}-${name}-disclaimer`}
                        >
                            {legend}
                        </legend>)}
                {choices
                    .map((obj, idx) => (
                        <div
                            key={`${formId}-${name}-${idx}`}
                            className="display-flex flex-wrap margin-1-t"
                        >
                            <input
                                id={`${formId}-${name}-${idx}`}
                                name={camelCase(name)}
                                data-step={camelCase(step)}
                                defaultChecked={dataValue
                                        && type === 'checkbox'
                                    ? dataValue.indexOf(obj.value) >= 0
                                    : dataValue === obj.value}
                                type={type}
                                value={obj.value}
                                required={required}
                                onChange={onUpdate}
                                onBlur={onUpdate}
                                onFocus={onUpdate}
                                disabled={Boolean(application.submitted_at)}
                            />
                            <label
                                id={`${formId}-${name}-${idx}-label`}
                                htmlFor={`${formId}-${name}-${idx}`}
                            >
                                {obj.label || obj.value}
                            </label>
                            {obj.conditionalTextInput
                                && (
                                    <div
                                        className="pad-3-l"
                                        style={{
                                            width: '100%',
                                            marginTop: 5
                                        }}
                                    >
                                        <input
                                            type="text"
                                            name={camelCase(`${name}-${obj.value}`)}
                                            data-step={camelCase(step)}
                                            data-conditional-value={obj.value}
                                            defaultValue={(application
                                                .data[camelCase(step)]
                                                    || {})[camelCase(`${name}-${obj.value}`)]}
                                            aria-labelledby={`${formId}-${name}-${idx}-label`}
                                            style={{ width: 200 }}
                                            disabled={dataValue
                                                    && type === 'checkbox'
                                                ? dataValue.indexOf(obj.value) < 0
                                                : dataValue !== obj.value}
                                            readOnly={Boolean(application.submitted_at)
                                                || !ebpcApplicationsOpen}
                                            onChange={onUpdate}
                                            onBlur={onUpdate}
                                            onFocus={onUpdate}
                                        />
                                    </div>
                                )}
                        </div>
                    ))}
                {required
                    && (
                        <p
                            id={`${formId}-${name}-invalid`}
                            role="alert"
                        >
                            {validationMessage || invalid || 'A choice is required'}
                        </p>)}
                {disclaimer
                    && (
                        <span
                            className="display-block body-4"
                            id={`${formId}-${name}-disclaimer`}
                            dangerouslySetInnerHTML={{
                                __html: disclaimer
                            }}
                            style={{
                                color: getCssVar('--slate'),
                                marginTop: 15
                            }}
                        />
                    )}
            </fieldset>
        )
    }

    const renderField = (field = {}, step = {}, fieldIdx = null) => {
        const keyPrefix = `dashboard-ebpc-application-${camelCase(step.slug)}`

        const getFieldKey = (obj = { props: {} }) =>
            `${keyPrefix}-${(obj.props || {}).name || obj.name || fieldIdx}`

        // Text block
        if ((field.type || '') === 'text'
                && (field.heading
                    || field.text))
            return (
                <div
                    className="margin-3-b"
                    key={getFieldKey(field)}
                >
                    {field.heading
                        && (
                            <h3 className="heading-5">
                                {field.heading}
                            </h3>)}
                    {field.text
                        && (
                            <p
                                className={`body-4${
                                    field.heading
                                        ? ' margin-1-t'
                                        : ''}`}
                                style={{
                                    color: getCssVar('--slate')
                                }}
                            >
                                {field.text}
                            </p>)}
                </div>
            )

        // Checkboxes & Radios
        if ((field.type || '').match(/^(checkbox|radio)$/)
                && field.name)
            return renderChoices(Object.assign(
                JSON.parse(JSON.stringify(field)), {
                    key: getFieldKey(field),
                    step: (step || {}).slug,
                    legend: `${field.legend || ''}${field.required
                        ? '*'
                        : ''}`
                }))

        // Label util
        const renderLabel = (
            inputId,
            text,
            className = null,
            labelProps = {}
        ) => inputId
            && text
            && (
                <label
                    htmlFor={inputId}
                    className={className}
                    {...(labelProps || {})}
                >
                    {text}
                </label>
            )

        // Upload
        if ((field.type || '') === 'upload'
                && (field.props || {}).name)
            return (
                <div
                    className="margin-3-b"
                    key={getFieldKey(field)}
                >
                    {field.label
                        && renderLabel(`${formId}-${field.props.name}`,
                            `${field.label}${field.props.required
                                ? '*'
                                : ''}`,
                            'font-weight-medium margin-1-b')}
                    {field.text
                        && <p className="body-4 margin-b">{field.text}</p>}
                    <DashboardSettingsInputUpload
                        id={`${formId}-${field.props.name}`}
                        data-step={camelCase(step.slug)}
                        preview={(application
                            .data[camelCase(step.slug)] || {})[camelCase(field.props.name)]
                                || ''}
                        onChange={onUpdate}
                        disabled={Boolean(application.submitted_at)
                            || !ebpcApplicationsOpen}
                        {...field.props}
                    />
                </div>
            )

        // Select
        if ((field.type || '') === 'select'
                && (field.props || {}).name
                && (field.options || []).length)
            return (
                <div
                    className="form-field margin-3-b"
                    key={`${getFieldKey(field)}`}
                >
                    {field.label
                        && renderLabel(`${formId}-${field.props.name}`,
                            `${field.label}${field.props.required
                                ? '*'
                                : ''}`,
                            'font-weight-medium margin-1-b')}
                    <select
                        id={`${formId}-${field.props.name}`}
                        data-step={camelCase(step.slug)}
                        defaultValue={(application
                            .data[camelCase(step.slug)] || {})[camelCase(field.props.name)]}
                        aria-describedby={field.props.required
                            ? `${formId}-${field.props.name}-invalid`
                            : null}
                        onChange={onUpdate}
                        onFocus={onUpdate}
                        onBlur={onUpdate}
                        disabled={Boolean(application.submitted_at)
                            || !ebpcApplicationsOpen}
                        {...field.props}
                    >
                        <option value="">Select an option</option>
                        {field.options
                            .filter(obj => 'value' in obj)
                            .map((obj, idx) => (
                                <option
                                    key={`${formId}-${field.props.name}-${idx}`}
                                    value={obj.value}
                                >
                                    {obj.label || obj.value}
                                </option>
                            ))}
                    </select>
                    {field.props.required
                        && (
                            <p
                                id={`${formId}-${field.props.name}-invalid`}
                                role="alert"
                            >
                                {field.invalid || 'An option is required'}
                            </p>)}
                </div>
            )

        // Text-based input util
        const renderInput = (obj) => {
            const Input = obj.type === 'textarea'
                ? TextArea
                : 'input'

            if (obj.type === 'input')
                obj.props.autoComplete = obj.props.autoComplete || 'on'

            return (
                <Input
                    id={`${formId}-${obj.props.name}`}
                    data-step={camelCase(step.slug)}
                    defaultValue={(application
                        .data[camelCase(step.slug)] || {})[camelCase(obj.props.name)]}
                    onChange={onUpdate}
                    onBlur={onUpdate}
                    onFocus={onUpdate}
                    readOnly={Boolean(application.submitted_at)
                        || !ebpcApplicationsOpen}
                    aria-describedby={obj.props.required
                            || obj.props.pattern
                        ? `${formId}-${obj.props.name}-invalid`
                        : null}
                    {...(obj.props || {})}
                />
            )
        }

        // Text-based inputs & Textareas
        if ((field.type || '').match(/^(input|textarea)$/)
                && (field.props || {}).name) {
            const isFloatingField = field.label
                && field.type === 'input'
                && field.props.placeholder === ' '

            return (
                <div
                    className={`form-field${
                        field.label
                                && field.props.placeholder === ' '
                            ? '-floating'
                            : ''}${
                        !field.gridClassName
                            ? ' margin-3-b'
                            : ''}`}
                    key={`${getFieldKey(field)}`}
                >
                    {!isFloatingField
                        && field.label
                        && renderLabel(`${formId}-${field.props.name}`,
                            `${field.label}${field.props.required
                                ? '*'
                                : ''}`,
                            'font-weight-medium margin-1-b')}
                    {field.text
                        && <p className="body-4 margin-b">{field.text}</p>}
                    {field.richText
                        && (
                            <RichText
                                className="body-4 margin-b"
                                html={field.richText}
                            />)}
                    {renderInput(field)}
                    {isFloatingField
                        && renderLabel(`${formId}-${field.props.name}`,
                            `${field.label}${field.props.required
                                ? '*'
                                : ''}`)}
                    {(field.required
                            || field.props.required
                            || field.props.pattern)
                        && (
                            <p
                                id={`${formId}-${field.props.name}-invalid`}
                                role="alert"
                            >
                                {field.invalid || 'A value is required'}
                            </p>)}
                </div>
            )
        }

        // Fieldset - Non-choices
        if (field.type === 'fieldset'
                && field.legend
                && (field.fields || []).length)
            return (
                <fieldset
                    className="form-field margin-3-b"
                    key={getFieldKey(field)}
                    data-key={getFieldKey(field)}
                >
                    <legend
                        className="font-weight-medium margin-1-b"
                    >
                        {field.legend}
                    </legend>
                    {field.fields
                        .map(obj => (
                            <div key={getFieldKey(obj)}>
                                {renderInput(obj)}
                            </div>
                        ))}
                </fieldset>
            )

        // Field groups
        if (field.type === 'group'
                && (field.fields || []).length)
            return (
                <div
                    className="row-condensed margin-3-b-md-up"
                    key={getFieldKey(field)}
                >
                    {field.fields
                        .map((obj, idx) => (
                            <div
                                className={obj.gridClassName
                                    || 'col col-12-md-down'}
                                key={`${getFieldKey(obj)}-container`}
                                data-key={`${getFieldKey(obj)}-container`}
                            >
                                {renderField(obj, step, idx)}
                            </div>
                        ))}
                </div>
            )

        return null
    }

    if (profile.id === null)
        return null

    const isStepValid = (slug) => {
        const requiredStepFieldNames = getEbpcFormDataObject(steps, true)

        const step = camelCase(slug || '')

        return !Object.keys(requiredStepFieldNames[step] || {})
            .filter(key => !(application.data[step] || {})[key])
            .length
    }

    const isLastStep = () => steps.length
        && (activeStep || {}).slug === (steps[steps.length - 1] || {}).slug

    return (
        <section
            className={`dashboard-ebpc-application dashboard-long-form pad-4-t pad-5-b-lg-up${
                activeStep
                    ? ' dashboard-ebpc-application-step dashboard-long-form-step'
                    : ''}${
                (!steps.filter(obj => !isStepValid(obj.slug)).length
                        || application.submitted_at)
                    ? ' dashboard-long-form-valid'
                    : ''}${
                application.submitted_at
                    ? ' dashboard-long-form-complete'
                    : ''}`}
            ref={el}
        >
            <div className="container">
                <div className="row flex-grow flex-justify-content-space-between">
                    <div
                        className={`display-flex flex-direction-column col-12${
                            !activeStep
                                ? ' col-7-lg-up'
                                : ''}`}
                    >
                        <div className="flex-none">
                            <Link
                                to={activeStep
                                    ? basePathname
                                    : '/dashboard'}
                                className="body-4 display-inline-flex"
                                style={{
                                    color: getCssVar('--slate'),
                                    marginBottom: 25,
                                    lineHeight: 1.1,
                                    overflow: 'hidden'
                                }}
                                onClick={(e) => {
                                    e.preventDefault()

                                    history.push(activeStep
                                        ? basePathname
                                        : '/dashboard')
                                }}
                            >
                                <svg
                                    className="icon-chevron-left flex-none"
                                    width="18"
                                    height="18"
                                    style={{ margin: '-2px 2px 0 -5px' }}
                                >
                                    <use xlinkHref="#icon-chevron-left" />
                                </svg>
                                <span>
                                    <span className="display-none-sm-down">Go back </span>
                                    <span className="text-transform-capitalize display-none-sm-up">back </span>
                                    to {!activeStep
                                        ? 'Dashboard'
                                        : 'Application summary'}
                                </span>
                            </Link>
                        </div>
                        {heading
                            && (
                                <div className="row">
                                    <h1
                                        id="dashboard-long-form-heading"
                                        className={`heading-2 col-12${
                                            !activeStep
                                                ? ' margin-1-b'
                                                : ' col-7-lg-up'}`}
                                        style={{ color: getCssVar('--navy') }}
                                    >
                                        {heading}
                                    </h1>
                                </div>)}
                        {!activeStep
                            && richText
                            && (
                                <RichText
                                    className="body-3"
                                    html={richText}
                                />
                            )}
                        {!activeStep
                            && (
                                <div className="row margin-3-t">
                                    {nextStep
                                        && !application.submitted_at
                                        && (
                                            <div className="col-auto">
                                                <Link
                                                    className="btn-secondary btn-lg"
                                                    to={`${basePathname}/${
                                                        nextStep.slug}`}
                                                >
                                                    {application.id
                                                        ? 'Continue'
                                                        : 'Begin'} Application
                                                </Link>
                                            </div>)}
                                </div>)}
                        {!activeStep
                            && Boolean(alerts.length)
                            && (
                                <div
                                    className={`dashboard-long-form-alerts margin-3-t-md-down${
                                        !application.submitted_at
                                            ? ' margin-t'
                                            : ''}`}
                                >
                                    {alerts
                                        .map((obj, idx) => (
                                            <Alert
                                                key={`ebpc-application-alert-${idx}`}
                                                className={`dashboard-long-form-alert${
                                                    obj.type
                                                        ? `-${obj.type}`
                                                        : ''}`}
                                                richText={obj.richText}
                                                type="info"
                                                role="status"
                                                dismissible={false}
                                                icon={obj.icon || true}
                                                fluid
                                            />
                                        ))}
                                </div>)}
                        <div className="row flex-justify-content-center margin-3-t margin-4-t-lg-up margin-3-b margin-5-b-lg-up">
                            <div
                                className={`col-12${
                                    activeStep
                                        ? ' col-10-xxl-up'
                                        : ''}`}
                            >
                                {activeStep
                                    && (
                                        <a
                                            href="#ebpc-application"
                                            className="skip-to body-3 margin-3-b"
                                        >
                                            Skip EBPC application navigation
                                        </a>)}
                                {(steps || []).length
                                    && (
                                        <nav
                                            className="dashboard-long-form-nav"
                                            aria-label="EBPC Application Sections"
                                        >
                                            <ol
                                                className={`row${activeStep
                                                    ? '-condensed flex-nowrap flex-justify-content-space-between-lg-down'
                                                    : ''}`}
                                            >
                                                {steps
                                                    .map((obj, idx) => (
                                                        <li
                                                            key={`dashboard-ebpc-application-nav-step-${idx}`}
                                                            className={`dashboard-long-form-nav-step${
                                                                `${basePathname}/${obj.slug}`
                                                                        === history.location.pathname
                                                                    ? ' dashboard-long-form-nav-step-active'
                                                                    : ''}${
                                                                isStepValid(obj.slug)
                                                                    ? ' dashboard-long-form-nav-step-valid'
                                                                    : ''}${
                                                                !activeStep
                                                                    ? ' col-12 margin-b'
                                                                    : ' col text-align-center'}`}
                                                        >
                                                            <Link
                                                                to={`${basePathname}/${
                                                                    obj.slug}`}
                                                                aria-current={`${basePathname}/${
                                                                    obj.slug}` === history.location.pathname}
                                                                data-step={camelCase(obj.slug)}
                                                                onClick={(e) => {
                                                                    if (activeStep)
                                                                        return

                                                                    e.preventDefault()
                                                                    e.stopPropagation()

                                                                    scrollTo([0, 0])
                                                                        .then(() => {
                                                                            e.target.focus()

                                                                            history.push(e.target
                                                                                .getAttribute('href'))
                                                                        })
                                                                }}
                                                            >
                                                                <div
                                                                    className={`row-condensed${
                                                                        activeStep
                                                                            ? ' margin-0'
                                                                            : ''}`}
                                                                >
                                                                    <div
                                                                        className={`dashboard-long-form-nav-step-index col-${
                                                                            activeStep
                                                                                ? '12 pad-0'
                                                                                : 'auto'}`}
                                                                        style={{
                                                                            '--step-index-line-horz-width-sm':
                                                                                `${100 / ((steps || [null]).length - 1)}%`,
                                                                            transitionDelay: idx
                                                                                    && idx !== steps.length - 1
                                                                                ? `${idx * 100}ms`
                                                                                : null
                                                                        }}
                                                                    >
                                                                        <span
                                                                            className="ratio-square round flex-align-items-center flex-justify-content-center"
                                                                            aria-label={`${idx + 1}.`}
                                                                        >
                                                                            {!isStepValid(obj.slug)
                                                                                ? <span className="display">{idx + 1}</span>
                                                                                : (
                                                                                    <svg
                                                                                        className={`icon-check flex-none${
                                                                                            activeStep
                                                                                                ? ' display-none-lg-down'
                                                                                                : ''}`}
                                                                                        width="18"
                                                                                        height="18"
                                                                                        aria-hidden="true"
                                                                                    >
                                                                                        <use xlinkHref="#icon-check" />
                                                                                    </svg>)}
                                                                        </span>
                                                                    </div>
                                                                    <div
                                                                        className={`col${
                                                                            activeStep
                                                                                ? ' pad-0'
                                                                                : ''}`}
                                                                    >
                                                                        <div
                                                                            className={!activeStep
                                                                                ? 'display-flex flex-nowrap pad-b'
                                                                                : null}
                                                                        >
                                                                            <div className="flex-grow">
                                                                                <p
                                                                                    className={`dashboard-long-form-step${
                                                                                        activeStep
                                                                                            ? '-nav'
                                                                                            : ''}-title`}

                                                                                >
                                                                                    <span>{obj.title}</span>
                                                                                </p>
                                                                                {!activeStep
                                                                                    && obj.text
                                                                                    && (
                                                                                        <span
                                                                                            className="body-4"
                                                                                            style={{ color: getCssVar('--slate') }}
                                                                                        >
                                                                                            {obj.text}
                                                                                        </span>)}
                                                                            </div>
                                                                            {!activeStep
                                                                                && (
                                                                                    <div className="flex-none display-inline-flex flex-align-items-center pad-l">
                                                                                        <svg
                                                                                            className="icon-chevron-right"
                                                                                            width="24"
                                                                                            height="24"
                                                                                        >
                                                                                            <use xlinkHref="#icon-chevron-right" />
                                                                                        </svg>
                                                                                    </div>)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Link>
                                                        </li>))}
                                            </ol>
                                        </nav>)}
                            </div>
                        </div>
                    </div>
                    {!activeStep
                        && faq.items.length
                        && (
                            <aside className="col-12 col-4-lg-up pad-5-t-lg-up">
                                {faq.heading
                                    && (
                                        <h2 className="heading-6 font-weight-medium margin-b">
                                            {faq.heading}
                                        </h2>)}
                                {faq.richText
                                    && (
                                        <RichText
                                            className="body-4 margin-b"
                                            html={faq.richText}
                                            style={{ lineHeight: 1.5 }}
                                        />)}
                                {faq.items
                                    .map((obj, idx) => (
                                        <Details
                                            className="card card-border margin-b"
                                            key={`accordions-${idx}`}
                                            {...obj}
                                        />
                                    ))}
                            </aside>)}
                </div>
                {activeStep
                    && application.id
                    && (
                        <div className="row flex-justify-content-center">
                            <div className="col-12 col-8-lg col-6-xl-up">
                                <h3
                                    id="dashboard-ebpc-application-step-title"
                                    className="dashboard-ebpc-application-step-title dashboard-long-form-step-title heading-5 display-flex flex-nowrap"
                                >
                                    <span className="dashboard-long-form-step-index flex-none display-flex flex-align-items-center display-none-lg-up margin-1-r">
                                        <span
                                            className="ratio-square round flex-align-items-center flex-justify-content-center"
                                            aria-label={`${steps.indexOf(activeStep) + 1}.`}
                                        >
                                            <span className="display">{steps.indexOf(activeStep) + 1}</span>
                                        </span>
                                    </span>
                                    <span className="flex-grow">
                                        <span>{activeStep.title}</span>
                                    </span>
                                </h3>
                                {activeStep.richText
                                    && (
                                        <RichText
                                            className="body-4 margin-b"
                                            html={activeStep.richText}
                                            style={{ lineHeight: 1.5 }}
                                        />)}
                                <Form
                                    id={formId}
                                    className="margin-t"
                                    aria-labelledby="dashboard-ebpc-application-step-title"
                                    aria-describedby="dashboard-epbc-application-form-state-indicator"
                                    tabIndex="-1"
                                >
                                    {(steps || [])
                                        .map((obj, idx) => (
                                            <div
                                                key={`dashboard-long-form-step-fields-${idx}`}
                                                className={`dashboard-long-form-step-fields ${
                                                    obj.slug === activeStep.slug
                                                        ? 'dashboard-long-form-step-fields-active'
                                                        : 'display-none'}`}
                                                data-step={camelCase(obj.slug)}
                                            >
                                                {(obj.fields || [])
                                                    .map((_obj, _idx) => renderField(_obj, obj, _idx))}
                                            </div>))}
                                </Form>
                                {isLastStep()
                                    && (
                                        <div
                                            className="dashboard-long-form-alerts dashboard-long-form-alerts-submission margin-t margin-4-t-sm-up"
                                        >
                                            <Alert
                                                className="dashboard-long-form-alert-submission-final"
                                                type="info"
                                                role="status"
                                                dismissible={false}
                                                icon
                                                fluid
                                            >
                                                <div className="rich-text">
                                                    <p>Submissions are final and cannot be edited following entry.</p>
                                                </div>
                                            </Alert>
                                            {!application.submitted_at
                                                && Boolean(steps
                                                    .filter((obj) => !isStepValid(obj.slug))
                                                    .length)
                                                && (
                                                    <Alert
                                                        className="dashboard-long-form-alerts-submission-error"
                                                        type="info"
                                                        role="status"
                                                        icon="icon-error"
                                                        dismissible={false}
                                                        fluid
                                                    >
                                                        <div className="rich-text">
                                                            <p className="margin-0-b">Application submission is dependent on all required fields being completed.</p>
                                                            <button
                                                                className="btn-unstyled"
                                                                onClick={(e) => {
                                                                    const focusInput = getInvalidInputs()[0]

                                                                    let step = focusInput
                                                                        && focusInput.getAttribute('data-step')

                                                                    if (!step)
                                                                        step = steps
                                                                            .filter(obj => !isStepValid(obj.slug))[0]

                                                                    if (step)
                                                                        gotoStep(step, focusInput)
                                                                }}
                                                            >
                                                                Click to return to the first remaining required field.
                                                            </button>
                                                        </div>
                                                    </Alert>)}

                                        </div>)}

                                <div className="row flex-nowrap flex-align-items-center margin-t margin-4-t-sm-up">
                                    <div
                                        id="dashboard-epbc-application-form-state-indicator"
                                        className="dashboard-long-form-state-indicator col body-4"
                                        ref={formStateIndicator}
                                    >
                                        <p>
                                            <svg
                                                className="icon-info"
                                                width="16"
                                                height="16"
                                            >
                                                <use xlinkHref="#icon-info" />
                                            </svg>
                                            <span>Progress is automatically saved</span>
                                        </p>
                                        <p
                                            data-form-state="saving"
                                            role="status"
                                        >
                                            <svg
                                                className="icon-sync"
                                                width="16"
                                                height="16"
                                            >
                                                <use xlinkHref="#icon-sync" />
                                            </svg>
                                            <span>Saving...</span>
                                        </p>
                                        <p
                                            data-form-state="saved"
                                            role="status"
                                        >
                                            <svg
                                                className="icon-check-circle"
                                                width="16"
                                                height="16"
                                            >
                                                <use xlinkHref="#icon-check-circle" />
                                            </svg>
                                            <span>Saved!</span>
                                        </p>
                                        <p
                                            data-form-state="error"
                                            role="alert"
                                        >
                                            <svg
                                                className="icon-error"
                                                width="16"
                                                height="16"
                                            >
                                                <use xlinkHref="#icon-error" />
                                            </svg>
                                            <span>An error occurred. Please try again.</span>
                                        </p>
                                    </div>
                                    <div className="col-auto">
                                        <div className="dashboard-long-form-controls">
                                            <div
                                                className={`row flex-align-items-center flex-justify-content-${
                                                    prevStep
                                                        ? 'space-between'
                                                        : 'end'}-lg-down`}
                                            >
                                                {(prevStep || {}).slug
                                                    && (
                                                        <div className="col-auto pad-1-r-sm-down">
                                                            <Link
                                                                className="font-weight-medium"
                                                                to={`${basePathname}/${
                                                                    prevStep.slug}`}
                                                                aria-label="Previous section"
                                                                style={{
                                                                    color: getCssVar('--slate')
                                                                }}
                                                                data-step={camelCase(prevStep.slug)}
                                                                onClick={onScrollToStep}
                                                            >
                                                                Previous
                                                            </Link>
                                                        </div>)}
                                                {(nextStep || {}).slug
                                                    && (
                                                        <div
                                                            className="col-auto pad-1-l-sm-down"
                                                        >
                                                            <Link
                                                                className="btn-secondary btn-lg"
                                                                to={`${basePathname}/${
                                                                    nextStep.slug}`}
                                                                aria-label="Next section"
                                                                data-step={camelCase(nextStep.slug)}
                                                                onClick={onScrollToStep}
                                                            >
                                                                Next
                                                            </Link>
                                                        </div>)}
                                                {!nextStep
                                                    && (
                                                        <div className="col-auto pad-1-l-sm-down">
                                                            <button
                                                                className="btn-secondary btn-lg pad-2-l-sm-down pad-r-sm-down"
                                                                type="submit"
                                                                form="ebpc-application"
                                                                disabled={!formValid
                                                                    || !ebpcApplicationsOpen
                                                                    || application.submitted_at}
                                                                onClick={onSubmit}
                                                            >
                                                                Submit application
                                                            </button>
                                                        </div>)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>)}
            </div>
        </section>
    )
}
